import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../data/constants';
import { getDieAndDescription } from '../../api/ebs-mfg-api/index.js';
import { useApiReady } from '@pella/fetch-client-react';

/**
 * Hook to fetch die number and item description from ebs-mfg given a valid item and orgID.
 * Returns a normalized shape: { data, isPending, error } where error is
 * a human-readable message string covering both API failures and soft
 * not-found cases (no die or description on the response).
 * @param {object} input
 * @param {number | undefined} input.organizationId
 * @param {string | undefined} input.item
 * @param {boolean} [input.enabled] - Optional override; when false the query is disabled regardless of other params
 */
const useDieNumberLookup = ({ organizationId, item, enabled: enabledOverride = true }) => {
  const { apiReady } = useApiReady();
  const query = useQuery({
    queryKey: [QUERY_KEYS.DIE_AND_DESCRIPTION, organizationId, item],
    queryFn: () =>
      getDieAndDescription({
        organizationId: /** @type {number} */ (organizationId),
        item: /** @type {string} */ (item),
      }),
    enabled: Boolean(enabledOverride && apiReady && organizationId && item),
  });

  const error =
    query.error?.message ||
    (query.isSuccess && (!query.data?.die || !query.data?.description)
      ? 'Invalid item. No die number or description found for the entered item number.'
      : null) ||
    null;

  return {
    data: query.data,
    isPending: query.isPending,
    error,
  };
};

export { useDieNumberLookup };
